// Converts real backend records into the exact row shapes the Calendar,
// Upcoming/DRHP, Subscription-status and Reviews pages already expect (see
// data/ipoCalendarData.js, upcomingIposData.js, ipoSubscriptionData.js,
// ipoReviewsData.js for the shapes these replace). Every component under
// components/{calendar,upcoming,subscription-status,ipo-reviews}/ stays
// unchanged -- only the array fed into each page is swapped from a static
// import to one of these adapters run over real API data.
import { toGmpSlug } from './gmpDataMapper';

// "18-Sep-2026" (backend drhp_date / NSE date format) -> "2026-09-18"
const MONTHS = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06', Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };
export const toIsoDate = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  if (match) {
    const [, day, mon, year] = match;
    const month = MONTHS[mon];
    if (month) return `${year}-${month}-${day.padStart(2, '0')}`;
  }
  // Already ISO (e.g. "2026-09-18" or "2026-09-18T10:00")
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;
};

// No dedicated mainboard/SME column anywhere in the schema -- reuses the
// same substring heuristic services/greymint_portal_mapper.py already uses
// server-side ("SME" if "sme" in ipo_type.lower() else ipo_type).
export const deriveBoard = (...signals) => (/sme|emerge/i.test(signals.filter(Boolean).join(' ')) ? 'sme' : 'mainboard');

export const parsePriceRange = (text) => {
  // NSE's issuePrice field: "Rs.140 to Rs.148" (or a single "Rs.148")
  // Match digits first so the "." in "Rs." isn't read as a decimal point
  const numbers = String(text || '').match(/\d+(?:\.\d+)?/g);
  if (!numbers) return { low: null, high: null };
  const [low, high] = numbers.map(Number);
  return { low, high: high ?? low };
};

// ---------------------------------------------------------------------------
// Calendar page (issue schedule) -- sourced from GET /api/market/ipos/live,
// the only place bid open/close dates are reliably populated right now (the
// automated discovery pipeline doesn't run NSE enrichment on every
// extraction the way the manual admin upload flow does, so most stored
// json_data.market_information blocks are still empty).
// ---------------------------------------------------------------------------
export const liveIpoToCalendarIssue = (liveIpo) => {
  const { low: priceLow, high: priceHigh } = parsePriceRange(liveIpo.issuePrice);
  const shares = Number(liveIpo.issueSize) || null;
  const avgPrice = priceHigh || priceLow;
  const sizeCr = shares && avgPrice ? Math.round(((shares * avgPrice) / 1e7) * 100) / 100 : null;
  return {
    id: liveIpo.symbol || liveIpo.companyName,
    company: liveIpo.companyName,
    board: deriveBoard(liveIpo.series),
    openOn: toIsoDate(liveIpo.issueStartDate),
    closeOn: toIsoDate(liveIpo.issueEndDate),
    priceLow,
    priceHigh,
    sizeCr,
    lotSize: liveIpo.lotSize ? Number(liveIpo.lotSize) : null,
    registrar: '',
    subscription: null,
    gmpSlug: liveIpo.companyName ? toGmpSlug(liveIpo.companyName) : null,
    hasProspectus: true,
  };
};

// ---------------------------------------------------------------------------
// Upcoming IPOs / DRHP pipeline page -- sourced from GET /api/core/ipos/{id}
// (mapIpoDetailResponse output). No backend field tracks SEBI's own
// observation-letter status (that's a regulatory concept our extraction
// pipeline doesn't parse), so every published record is treated as
// "approved" in the one sense we can actually verify -- its DRHP has been
// extracted and published here -- and "confidential"/pre-filing rows are
// left empty rather than guessed.
// ---------------------------------------------------------------------------
export const detailToDrhpFiling = (detail) => {
  const board = deriveBoard(detail.type, detail.exchanges);
  const offer = detail.freshIssue && detail.ofs ? 'fresh_ofs' : detail.freshIssue ? 'fresh' : detail.ofs ? 'ofs' : 'pre_filed';
  return {
    id: detail.id,
    company: detail.companyName,
    sector: detail.productCategories?.[0] || detail.industrySummary?.split('.')[0]?.slice(0, 60) || '—',
    board,
    offer,
    filedOn: toIsoDate(detail.raw?.drhp_date) || toIsoDate(detail.raw?.created_at),
    approvedOn: null,
    size: detail.freshIssue || detail.totalIssueSize || 'Size to be announced',
    sizeNote: detail.ofs ? `OFS: ${detail.ofs}` : '',
    exchanges: detail.exchanges ? detail.exchanges.split(/,\s*/).filter(Boolean) : [],
    leadManager: detail.leadManagerObj?.name || '',
    status: 'approved',
    confidential: false,
  };
};

// ---------------------------------------------------------------------------
// IPO Reviews page -- sourced from GET /api/core/ipos/{id}/broker-reviews
// (individual broker calls + a server-computed rating_counts summary, see
// backend/shared/broker_reviews.py). No field anywhere supplies a Capital
// Market (CM) score -- that's a third-party proprietary rating, not
// something this pipeline computes -- so cmScore stays null and the page's
// own existing "awaited" styling (already built for null cmScore) is what
// renders for every company, same as when a real research desk simply
// hasn't published one yet.
// ---------------------------------------------------------------------------
export const RATING_TONE = {
  Subscribe: 'positive',
  'Subscribe with caution': 'cautious',
  Neutral: 'neutral',
  Avoid: 'negative',
  'Not Rated': 'neutral',
};

export const consensusFromRatingCounts = (ratingCounts, totalReviews) => {
  if (!totalReviews) return 'awaited';
  const [topRating] = Object.entries(ratingCounts || {}).sort((a, b) => b[1] - a[1])[0] || [];
  if (topRating === 'Subscribe') return ratingCounts[topRating] === totalReviews ? 'must_subscribe' : 'apply';
  if (topRating === 'Subscribe with caution') return 'may_apply';
  if (topRating === 'Avoid') return 'avoid';
  return 'neutral';
};

// The parts of a review row that come from GET /api/core/ipos/{id}/broker-reviews
const reviewFieldsFromBrokerPayload = (payload) => {
  const reviews = payload?.reviews || [];
  const summary = payload?.summary || { total_reviews: 0, rating_counts: {} };
  return {
    ratings: reviews.map((r) => ({ analyst: r.broker_name, call: r.rating, tone: RATING_TONE[r.rating] || 'neutral' })),
    note: summary.total_reviews > 0 ? summary.consensus : 'Broker consensus not yet available for this company.',
    noteStyle: summary.total_reviews > 0 ? 'plain' : 'pending',
    consensus: consensusFromRatingCounts(summary.rating_counts, summary.total_reviews),
  };
};

export const detailAndReviewsToReviewRow = (detail, brokerReviewsPayload) => {
  const board = deriveBoard(detail.type, detail.exchanges);
  return {
    ...reviewFieldsFromBrokerPayload(brokerReviewsPayload),
    id: detail.id,
    company: detail.companyName,
    sector: detail.productCategories?.[0] || '—',
    board,
    platform: board === 'sme' ? (detail.exchanges?.includes('NSE') ? 'NSE SME' : 'BSE SME') : 'Mainboard',
    cmScore: null,
    report: null,
    updatedOn: toIsoDate(detail.raw?.updated_at) || toIsoDate(detail.raw?.created_at) || new Date().toISOString().slice(0, 10),
  };
};

/**
 * The same review row, built from a GET /api/core/ipos *list* item instead of
 * a fetched detail.
 *
 * The list already carries every field this page shows -- name, issue type,
 * listing exchange and the timestamps -- so the page does not fetch one detail
 * per id to get them. The single field only a detail holds is the sector
 * (json_data.business_information.product_categories), and the extractor
 * fills that in for a small minority of filings, which is not worth a request
 * per row.
 */
export const catalogItemAndReviewsToReviewRow = (item, brokerReviewsPayload) => {
  const exchanges = item.listing_exchange || '';
  const board = deriveBoard(item.ipo_type, exchanges);
  return {
    ...reviewFieldsFromBrokerPayload(brokerReviewsPayload),
    id: item.id,
    company: item.company_name || item.brand_name || '',
    sector: '—',
    board,
    platform: board === 'sme' ? (exchanges.includes('NSE') ? 'NSE SME' : 'BSE SME') : 'Mainboard',
    cmScore: null,
    report: null,
    updatedOn: toIsoDate(item.updated_at) || toIsoDate(item.created_at) || toIsoDate(item.drhp_date) || null,
  };
};
